import Button from '@ssg/common/Components/Button';
import Header from '@ssg/common/Components/Header';
import { removeTypenameGeneric } from '@ssg/common/Helpers/typenameHelper';
import { useStorageState } from '@ssg/common/Hooks/useLocalStorage';
import { assessmentPoints } from '@ssg/common/Configurations/AssesmentPoints';
import { inspectionAssesmentSignatureLines } from '@ssg/common/Configurations/InspectionAssesmentSignatureLines';
import React, { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import Box from '../../../Components/Layout/Box';
import TextAreaInput from './Inputs/TextAreaInput';
import ReportStaticInfo from './ReportStaticInfo';
import { faCheck, faTimes, faFilePdf, faExclamationTriangle, faEdit } from '@fortawesome/pro-regular-svg-icons';
import { useFlag } from '@unleash/proxy-client-react';
import Loading from '@ssg/common/Components/Loading';
import { FeatureFlagEnums } from '@ssg/common/FeatureFlagEnums';
import {
	AssesmentColor,
	AssesmentInput,
	DeviationColor,
	DeviationInput,
	EsdhFileFragmentFragment,
	InputMaybe,
	SignatureInputType,
	useCreateInspectionAssesmentReportMutation,
	useGetCaseAsbestosReportsAndDocumentsQuery,
	useGetWebCaseQuery,
} from '@ssg/common/GraphQL/indexV2';
import Radio from '@ssg/common/Components/Radio';
import { SelectOption } from '@ssg/common/Helpers/Helpers';
import { tryGetFiles } from './checkFilesExist';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { formatDateForInput } from '@ssg/common/Helpers/dateToDateOnlyString';
import Datepicker from '@ssg/common/Components/Datepicker';
import FormFieldHeader from '@ssg/common/Components/FormFieldHeader';
import TextInput from './Inputs/TextInput';
import SignatureInput from './Inputs/SignatureInput';
import { useUploadSignature } from './hooks/useUploadSignature';
import Modal from '@ssg/common/Components/Modal';

interface Params {
	id: string;
}
interface ReportError {
	key: string;
}
interface SigningWorker {
	workerIndex: number;
}
interface DeviationError {
	assesmentId: number;
	key: keyof DeviationInput;
}

const colorSelectionOptions: SelectOption[] = [
	{ label: 'reports.asbestos.green', value: AssesmentColor.Green },
	{ label: 'reports.asbestos.yellow', value: AssesmentColor.Yellow },
	{ label: 'reports.asbestos.red', value: AssesmentColor.Red },
];

const DeviationColorOptions: SelectOption[] = [
	{ label: 'reports.asbestos.yellow', value: DeviationColor.Yellow },
	{ label: 'reports.asbestos.red', value: DeviationColor.Red },
];

const throwErrorMessage = (key: string, reportErrors: ReportError[]): string | undefined => {
	const isError = reportErrors.filter(re => re.key === key).length > 0 || key === undefined;
	if (isError) return 'error.requiredShort';
	return;
};

const throwDeviationError = (assesmentId: number, key: keyof DeviationInput, deviationErrors: DeviationError[]): string | undefined => {
	const isError = deviationErrors.filter(re => re.assesmentId === assesmentId && re.key === key).length > 0;
	if (isError) return 'error.required';
	return;
};

const EMPTY_DEVIATION_FORM = (value: AssesmentColor): DeviationInput => ({
	reportDate: formatDateForInput(new Date()),
	relatedEvaluationCategory: '',
	relatedEvaluationQuestion: '',
	action: '',
	actionDate: null,
	actionResponsible: '',
	cause: '',
	description: '',
	severityLevel: value === AssesmentColor.Red ? DeviationColor.Red : DeviationColor.Yellow,
});

const InspectionAssesmentReportForm: React.FC = (): React.ReactElement => {
	const { id: caseId } = useParams<Params>();
	const { t } = useTranslation();
	const history = useHistory();

	const saveButtonReportsFlag = useFlag(FeatureFlagEnums.SAVE_BUTTON_REPORTS);
	const { data: caseData } = useGetWebCaseQuery({ variables: { id: caseId } });
	const erpNumber = caseData?.case.erpNo || '';
	const { loading: caseReportsAndDocumentsLoading } = useGetCaseAsbestosReportsAndDocumentsQuery({
		variables: {
			id: caseId,
		},
		skip: !caseId || caseId === '',
		fetchPolicy: 'cache-and-network',
		onCompleted(data) {
			const formData = removeTypenameGeneric(data.case.caseAsbestosReports?.inspectionAssesmentFormData);
			if (formData) {
				if (formData.assesments !== null) {
					const sanitizedAssesments = removeTypenameGeneric(formData.assesments);
					setAssesments(sanitizedAssesments as AssesmentInput[]);
				}
				if (formData.signatures !== null) {
					const sanitizedSignatures = removeTypenameGeneric(formData.signatures);
					setSignatures(sanitizedSignatures as SignatureInputType[]);
					const nonNullFiles = sanitizedSignatures
						.filter(signature => signature !== null && signature !== undefined && signature.signatureDetails !== null && signature.signatureDetails !== undefined)
						.map(signature => signature!.signatureDetails!.fileName);

					const files = tryGetFiles(nonNullFiles, data.case.documents);

					sanitizedSignatures.forEach(signature => {
						if (signature?.index === null || signature?.index === undefined) {
							return;
						}
						const file = files.find(file => file.name === signature.signatureDetails?.fileName) ?? null;
						setSignatureFiles(prevFiles => {
							const updatedFiles = [...(prevFiles || [])];
							updatedFiles[signature.index!] = file;
							return updatedFiles;
						});
					});
				}
				setActiveTab(assesments.find(a => a.deviation !== undefined)?.id ?? 0);
			}
		},
	});

	const useLocalStorage = <T,>(name: string, initialValue: T) => useStorageState<T>(window.localStorage, `${caseId}-inspection-assesment${name}`, initialValue);

	const [assesments, setAssesments] = useLocalStorage<AssesmentInput[]>('inspectionAssesmentQuestions', populateEmptyLocalstorage());
	const [activeTab, setActiveTab] = React.useState<number>(0);
	const [signatures, setSignatures] = useLocalStorage<InputMaybe<InputMaybe<SignatureInputType>[]> | undefined>('signatures', populateSignaturesInLocaleStorage());
	const [signingWorker, setSigningWorker] = React.useState<SigningWorker>();
	const [signatureFiles, setSignatureFiles] = React.useState<(EsdhFileFragmentFragment | null)[]>();
	const [fileErrors, setFileErrors] = React.useState<boolean[]>([]);
	// Function to dynamically update a specific question
	const setColor = (assesmentId: number, value: AssesmentColor) => {
		setAssesments(oldAssesments => oldAssesments.map(assesment => (assesment.id === assesmentId ? { ...assesment, color: value } : assesment)));
		if (value === AssesmentColor.Red || value === AssesmentColor.Yellow) {
			addAssesmentDeviation(assesmentId);
		}
		if (value === AssesmentColor.Green) {
			removeAssesmentDeviation(assesmentId);
		}
	};
	const setComment = (index: number, value: string) => {
		setAssesments(oldAssesments => oldAssesments.map(assesment => (assesment.id === index ? { ...assesment, comment: value } : assesment)));
	};

	const addAssesmentDeviation = (assesmentIndex: number) => {
		setAssesments(oldAssesments =>
			oldAssesments.map(assesment =>
				assesment.id === assesmentIndex
					? {
							...assesment,
							deviation:
								assesment.deviation == null
									? EMPTY_DEVIATION_FORM(assesment.color)
									: { ...assesment.deviation, severityLevel: assesment.color === AssesmentColor.Red ? DeviationColor.Red : DeviationColor.Yellow },
					  }
					: assesment,
			),
		);
		setActiveTab(assesmentIndex);
	};

	const removeAssesmentDeviation = (assesmentIndex: number) => {
		setAssesments(oldAssesments => oldAssesments.map(assesment => (assesment.id === assesmentIndex ? { ...assesment, deviation: undefined } : assesment)));
		setActiveTab(assesments.find(a => a.deviation !== undefined)?.id ?? 0);
	};

	const handleAssesmentDeviationChange = (index: number, key: keyof DeviationInput, value: DeviationInput[keyof DeviationInput]) => {
		setAssesments(oldAssesments =>
			oldAssesments.map(assesment =>
				assesment.id === index
					? {
							...assesment,
							deviation: assesment.deviation ? { ...assesment.deviation, [key]: value } : undefined,
					  }
					: assesment,
			),
		);
	};

	const [submitted, setSubmitted] = React.useState(false);
	const [saved, setSaved] = React.useState(false);
	const [loadingType, setLoadingType] = React.useState<'CREATE' | 'SAVE'>('CREATE');
	const [reportErrors, setReportErrors] = React.useState<ReportError[]>([]);
	const [deviationErrors, setDeviationErrors] = React.useState<DeviationError[]>([]);
	const [createInspectionAssesmentReport, { loading: submitting }] = useCreateInspectionAssesmentReportMutation();

	const errorCheck = (): boolean => {
		const assesmentErrors: ReportError[] = [];
		const deviationErrors: DeviationError[] = [];
		assesments.forEach(assesment => {
			const colorSelection = assesment?.color;
			const deviation = assesment.deviation;

			if (colorSelection === AssesmentColor.NonSelected || colorSelection === undefined) {
				assesmentErrors.push({ key: `selection-${assesment?.id}` });
			}
			if (deviation?.reportDate === '' || deviation?.reportDate === 'NaN-aN-aN') {
				deviationErrors.push({ assesmentId: assesment.id, key: 'reportDate' });
			}
			if (deviation?.severityLevel === DeviationColor.NonSelected) {
				deviationErrors.push({ assesmentId: assesment.id, key: 'severityLevel' });
			}
			if (deviation?.description === '') {
				deviationErrors.push({ assesmentId: assesment.id, key: 'description' });
			}
			if (deviation?.cause === '') {
				deviationErrors.push({ assesmentId: assesment.id, key: 'cause' });
			}
		});
		setReportErrors(assesmentErrors);
		setDeviationErrors(deviationErrors);
		return assesmentErrors.length > 0 || deviationErrors.length > 0;
	};

	async function submitReport() {
		setSubmitted(true);
		if (errorCheck()) {
			return;
		}
		try {
			setLoadingType('CREATE');
			await createInspectionAssesmentReport({
				variables: {
					caseId,
					formData: { assesments, signatures },
					onlySave: false,
				},
			});

			history.goBack();
		} catch (e) {
			console.error(e);
		}
	}
	React.useEffect(() => {
		setSaved(false);
	}, [assesments]);

	async function saveReport() {
		try {
			setLoadingType('SAVE');
			await createInspectionAssesmentReport({
				variables: {
					caseId,
					formData: { assesments, signatures },
					onlySave: true,
				},
			});
			setSaved(true);
		} catch (e) {
			console.error(e);
		}
	}

	const { isUploading, uploadSignature } = useUploadSignature();
	if (caseReportsAndDocumentsLoading) return <Loading />;

	return (
		<div>
			<Header title="reports.reportTypes.inspectionAssesment" backButton />

			<div className="space-y-8 px-10 pb-10 text-sm">
				<ReportStaticInfo caseId={caseId} />

				<Box full>
					{assessmentPoints.map(point => {
						const assesmentSelect = assesments.find(assesment => assesment.id === point.index)?.color ?? AssesmentColor.NonSelected;
						const comment = assesments.find(assesment => assesment.id === point.index)?.comment ?? '';
						return (
							<Fragment key={point.index}>
								<div className="mt-4 text-base font-semibold text-blue">{`Pkt. ${point.index}. ${point.assesmentPoint}`}</div>
								<div className="flex-col gap-3">
									<Radio
										horizontal
										className="mt-5"
										title=""
										options={colorSelectionOptions}
										name={`question-${point.index}`}
										onChange={e => setColor(point.index, e.target.value as AssesmentColor)}
										value={assesmentSelect}
										errorMessage={throwErrorMessage(`selection-${point.index}`, reportErrors)}
									/>
									<TextAreaInput label="" placeholder={t('reports.asbestos.remark')} value={comment} onChange={value => setComment(point.index, value)} />
								</div>
							</Fragment>
						);
					})}
					{assesments.find(a => a.deviation !== undefined) && (
						<>
							<div className="text-2xl mt-4 font-bold text-blue">{t('reports.asbestos.deviationTitles')}</div>
							<div className="flex flex-wrap">
								<div className="w-full">
									<ul className="mb-0 flex list-none flex-row flex-wrap pb-4" role="tablist">
										{assesments.map(
											(assesment, idx) =>
												assesment.deviation !== undefined &&
												assesment.deviation !== null && (
													<li key={assesment.id} className="-mb-px mr-2 flex-auto text-center last:mr-0">
														{idx === 0}{' '}
														<a
															className={
																'block rounded mt-2 px-5 py-3 text-xs font-bold uppercase leading-normal shadow-lg ' +
																(activeTab === assesment.id ? 'bg-blue-calm text-white' : 'text-blue-calm bg-white')
															}
															onClick={e => {
																e.preventDefault();
																setActiveTab(assesment.id);
															}}
															data-toggle="tab"
															href={'#link' + assesment.id}
															role="tablist"
														>
															{typeof deviationErrors.find(deviation => deviation.assesmentId === assesment.id) !== 'undefined' && (
																<FontAwesomeIcon icon={faExclamationTriangle} className="text-red mr-1" size="lg" />
															)}
															{`pkt.${assesment.id}`}
														</a>
													</li>
												),
										)}

										<li className="-mb-px mr-2 flex-auto text-center last:mr-0"></li>
									</ul>
									<div className="relative mb-6 flex w-full min-w-0 flex-col break-words rounded bg-white shadow-lg">
										<div className="flex-auto px-4 py-5">
											<div className="tab-content tab-space">
												{assesments.map(
													(assesment, idx) =>
														assesment.deviation !== undefined &&
														assesment.deviation !== null && (
															<div key={assesment.id} className={activeTab === assesment.id ? 'block' : 'hidden'} id={'#link' + assesment.id}>
																<FormFieldHeader
																	largeTitle
																	title={`${t('reports.asbestos.deviationTitle')} - ${assessmentPoints.find(ap => ap.index === assesment.id)?.assesmentPoint}`}
																/>
																<div className="mt-4">
																	<Datepicker
																		title={'reports.asbestos.deviationReportDate'}
																		name={`deviationReportDate-${assesment.id}`}
																		value={assesment.deviation?.reportDate}
																		onChange={e => handleAssesmentDeviationChange(assesment.id, 'reportDate', e.target.value)}
																		errorMessage={throwDeviationError(assesment.id, 'reportDate', deviationErrors)}
																	/>
																	<Radio
																		name={`severityLevel-${assesment.id}`}
																		options={DeviationColorOptions}
																		title={'reports.asbestos.deviationSeverity'}
																		onChange={e => {
																			const color = e.target.value as DeviationColor;
																			handleAssesmentDeviationChange(assesment.id, 'severityLevel', color);
																		}}
																		value={assesment.deviation?.severityLevel}
																		horizontal
																		errorMessage={throwDeviationError(assesment.id, 'severityLevel', deviationErrors)}
																	/>
																	<TextAreaInput
																		label="reports.asbestos.description"
																		value={assesment.deviation?.description}
																		onChange={e => handleAssesmentDeviationChange(assesment.id, 'description', e)}
																		placeholder={'reports.asbestos.descriptionPlaceholder'}
																		errorMessage={throwDeviationError(assesment.id, 'description', deviationErrors)}
																	/>
																	<TextAreaInput
																		label="reports.asbestos.cause"
																		value={assesment.deviation?.cause}
																		onChange={e => handleAssesmentDeviationChange(assesment.id, 'cause', e)}
																		placeholder={'reports.asbestos.causePlaceholder'}
																		errorMessage={throwDeviationError(assesment.id, 'cause', deviationErrors)}
																	/>
																	<TextAreaInput
																		label="reports.asbestos.correctingAction"
																		value={assesment.deviation?.action}
																		onChange={e => handleAssesmentDeviationChange(assesment.id, 'action', e)}
																		placeholder={'reports.asbestos.actionPlaceholder'}
																	/>
																	<TextInput
																		label={'reports.asbestos.actionResponsible'}
																		value={assesment.deviation?.actionResponsible}
																		onChange={e => handleAssesmentDeviationChange(assesment.id, 'actionResponsible', e)}
																	/>
																	<Datepicker
																		title={'reports.asbestos.actionDate'}
																		name={`actionDate-${assesment.id}`}
																		value={assesment.deviation?.actionDate ?? ''}
																		onChange={e =>
																			e.target.value === '' || e.target.value === 'NaN-aN-aN'
																				? handleAssesmentDeviationChange(assesment.id, 'actionDate', null)
																				: handleAssesmentDeviationChange(assesment.id, 'actionDate', e.target.value)
																		}
																	/>
																</div>
															</div>
														),
												)}
											</div>
										</div>
									</div>
								</div>
							</div>
						</>
					)}
					<div className="mt-4 text-base font-semibold text-blue">{t('reports.asbestos.signatures')}</div>
					{signatures &&
						signatures.map((signature, index) => {
							const signed = signatureFiles?.some(
								file => signature?.signatureDetails !== undefined && signature.signatureDetails !== null && file?.name === signature.signatureDetails.fileName,
							);

							return (
								<div key={signature?.index} className="flex mt-2 gap-3">
									<div className="  font-medium text-blue w-22">{inspectionAssesmentSignatureLines[index]} </div>
									{signed ? (
										<div className="align-center">
											<FontAwesomeIcon icon={faCheck} className="text-green" size="lg" />
										</div>
									) : (
										<div className="align-center">
											<FontAwesomeIcon icon={faTimes} className="text-red" size="lg" />
										</div>
									)}
									<FontAwesomeIcon
										icon={faEdit}
										size="lg"
										className="cursor-pointer"
										onClick={() => {
											setSigningWorker({ workerIndex: index });
										}}
									/>
								</div>
							);
						})}
				</Box>

				<div className="ml-4 my-2 mr-6 flex flex-row space-x-2">
					<Button icon={faFilePdf} onClick={() => submitReport()} disabled={submitting} loading={submitting && loadingType === 'CREATE'} success text="reports.createPdfReport" />

					{saveButtonReportsFlag && (
						<Button
							disabled={submitting}
							loading={submitting && loadingType === 'SAVE'}
							primary
							onClick={() => saveReport()}
							text={saved ? '' : 'reports.saveReport'}
							icon={saved ? faCheck : undefined}
						/>
					)}
				</div>
			</div>
			{signingWorker && signingWorker.workerIndex !== undefined && (
				<Modal
					visible={signingWorker.workerIndex !== undefined}
					close={() => {
						setSigningWorker(undefined);
					}}
					title={''}
					body={
						<div className="p-1">
							<SignatureInput
								required={false}
								label={t('reports.asbestos.signature')}
								isUploading={isUploading}
								signatureFile={signatureFiles?.[signingWorker.workerIndex] ?? undefined}
								onClear={() => {
									const index = signingWorker.workerIndex;
									setSignatureFiles(prevFiles => {
										const updatedFiles = [...(prevFiles || [])];
										updatedFiles[index] = null;
										return updatedFiles;
									});
									setSignatures(prevFiles => {
										const updatedFiles = [...(prevFiles || [])];
										updatedFiles[index] = { index, signatureDate: null, signatureDetails: null };

										return updatedFiles;
									});
								}}
								onSubmit={async signature => {
									try {
										const fileName = `signature-${Date.now()}.${signature.extension}`;
										const uploadResponse = await uploadSignature(signature.base64, fileName, erpNumber);
										const index = signingWorker.workerIndex;
										if (uploadResponse?.ok) {
											setSignatureFiles(prevFiles => {
												const updatedFiles = [...(prevFiles || [])];
												updatedFiles[index] = {
													thumbnail: signature.base64,
													extension: signature.extension,
													url: '',
													created: '',
													name: fileName,
													fileId: '',
													groupingKey: '',
													metadata: [],
													comments: '',
												};
												setSignatures(prevFiles => {
													const updatedFiles = [...(prevFiles || [])];
													updatedFiles[index] = { index, signatureDate: formatDateForInput(new Date()), signatureDetails: { fileName } };

													return updatedFiles;
												});
												setSigningWorker(undefined);
												return updatedFiles;
											});
											// handleChange(signingWorker.workerIndex, 'signature', { signatureDate: formatDateForInput(new Date()), signatureDetails: { fileName: fileName } });
										} else {
											setSignatureFiles(prevFiles => {
												const updatedFiles = [...(prevFiles || [])];
												updatedFiles[signingWorker.workerIndex] = null;
												return updatedFiles;
											});
										}
									} catch (error) {
										console.error(error);
										setFileErrors(prevErrors => {
											const updatedErrors = [...(prevErrors || [])];
											updatedErrors[signingWorker.workerIndex] = true;
											return updatedErrors;
										});
									}
								}}
								errorMessage={fileErrors[signingWorker.workerIndex] ? t('common.couldNotUpload') : undefined}
							/>
						</div>
					}
				/>
			)}
		</div>
	);
};

export default InspectionAssesmentReportForm;

const populateEmptyLocalstorage = (): AssesmentInput[] => {
	const initialData: AssesmentInput[] = [];
	assessmentPoints.forEach(point => {
		initialData.push({ id: point.index, comment: '', color: AssesmentColor.NonSelected });
	});
	return initialData;
};

const populateSignaturesInLocaleStorage = (): SignatureInputType[] => {
	const initialData: SignatureInputType[] = [];
	inspectionAssesmentSignatureLines.forEach((line, index) => {
		initialData.push({ index, signatureDate: undefined, signatureDetails: null });
	});
	return initialData;
};
